import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, Download, ShieldCheck } from 'lucide-react';
import guestApi, { loadSession } from '../api/guest';
import { formatDate, formatSize, previewType } from '../lib/files';
import { LampMark } from '../components/drive/AppShell';
import { FileTag } from '../components/drive/ItemsView';
import { startDownload } from '../components/drive/useItemActions';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const errorText = (err, fallback) => {
  const body = err?.response?.data;
  if (body?.detail) return body.detail;
  if (body?.code) return body.code[0];
  return fallback;
};

const Shell = ({ email, children }) => (
  <div className="flex min-h-screen flex-col bg-background text-foreground">
    <header className="flex flex-wrap items-center gap-3 bg-ink px-6 py-3.5 text-ink-foreground">
      <div className="flex items-center gap-2.5">
        <LampMark className="h-7 w-7 rounded-lg bg-ink" />
        <span className="text-base font-semibold">LampTwist <span className="font-normal opacity-75">Drive</span></span>
      </div>
      {email && (
        <span className="ml-auto flex items-center gap-2 text-sm opacity-90">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Verified as {email}
        </span>
      )}
    </header>
    <main className="mx-auto flex w-full max-w-[1180px] flex-1 flex-col gap-6 px-4 py-8 sm:px-6">{children}</main>
    <footer className="mx-auto flex w-full max-w-[1180px] flex-wrap justify-between gap-3 border-t border-border px-4 py-4 text-xs text-muted-foreground sm:px-6">
      <span>Downloads are logged and visible to the person who shared this.</span>
      <span>No account needed</span>
    </footer>
  </div>
);

const Verify = ({ info, api, onVerified }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.requestCode(email.trim());
      setStep('code');
    } catch (err) {
      setError(errorText(err, 'Could not send a code. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.verify({ email: email.trim(), code: code.trim() });
      onVerified();
    } catch (err) {
      setError(errorText(err, 'That code is not valid or has expired.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm">
      <p className="text-sm text-muted-foreground">{info.shared_by} shared</p>
      <h1 className="mt-1 text-2xl font-semibold tracking-[-0.015em]">{info.name}</h1>
      {step === 'email' ? (
        <form onSubmit={sendCode} className="mt-6 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Your email address</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-xl border border-input bg-background px-3 outline-none focus:border-primary"
            />
          </label>
          <p className="text-xs text-muted-foreground">Use the address this was shared with. We’ll email you a 6-digit code.</p>
          <button type="submit" disabled={busy} className="h-11 rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">Email me a code</button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 flex flex-col gap-3">
          <p className="text-sm">If <b>{email}</b> was invited, a code is on its way.</p>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">6-digit code</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="h-11 rounded-xl border border-input bg-background px-3 tabular text-lg tracking-[0.3em] outline-none focus:border-primary"
            />
          </label>
          <button type="submit" disabled={busy} className="h-11 rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">Open</button>
          <button type="button" onClick={() => { setStep('email'); setCode(''); }} className="h-10 text-sm font-semibold text-primary">Use another email</button>
        </form>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
    </div>
  );
};

const GuestPortal = () => {
  const { token } = useParams();
  const [session, setSession] = useState(() => loadSession(token));
  const api = useMemo(() => guestApi(token, () => session), [token, session]);
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [folderId, setFolderId] = useState(null);
  const [listing, setListing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    api.info().then(setInfo).catch(() => setFailed(true));
  }, [api]);

  // "Anyone with the link" needs no code: open a session straight away
  useEffect(() => {
    if (info && !info.verified && info.mode === 'anyone') {
      api.verify({}).then(setSession).catch(() => setFailed(true));
    }
  }, [info, api]);

  useEffect(() => {
    if (!info?.verified) return;
    setSelected(new Set());
    api.contents(folderId).then(setListing).catch(() => setFailed(true));
  }, [info, folderId, api]);

  if (failed) {
    return (
      <Shell>
        <div className="mx-auto max-w-md py-16 text-center">
          <h1 className="text-2xl font-semibold">This link is no longer available</h1>
          <p className="mt-2 text-muted-foreground">It may have expired or been turned off. Ask the person who shared it for a new link.</p>
        </div>
      </Shell>
    );
  }
  if (!info) return <Shell><div className="h-40 animate-pulse rounded-2xl bg-muted" /></Shell>;
  if (!info.verified) {
    if (info.mode === 'anyone') return <Shell><div className="h-40 animate-pulse rounded-2xl bg-muted" /></Shell>;
    return (
      <Shell>
        <Verify info={info} api={api} onVerified={() => setSession(loadSession(token))} />
      </Shell>
    );
  }

  const items = listing?.items || [];
  const root = listing?.breadcrumbs?.[0];
  const chosen = items.filter((i) => selected.has(i.id));
  const totalSize = chosen.reduce((s, i) => s + (i.size || 0), 0);
  const allChecked = items.length > 0 && chosen.length === items.length;

  const download = async (ids, zipping) => {
    try {
      if (zipping) setNotice('Preparing your .zip — the download starts in a moment.');
      startDownload(await api.downloadUrl(ids));
    } catch (err) {
      setNotice(errorText(err, 'Download failed.'));
    }
    setTimeout(() => setNotice(''), 5000);
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const openItem = (item) => {
    if (item.kind === 'folder') setFolderId(item.id);
    else if (previewType(item)) setPreview(item);
    else if (info.allow_download) download([item.id], false);
  };

  return (
    <Shell email={info.email}>
      <div className="flex flex-wrap items-end gap-5">
        <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-2">
          <span className="text-sm text-muted-foreground">From {info.shared_by}</span>
          <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.015em]">{info.name}</h1>
          <div className="flex flex-wrap gap-2 text-[13px]">
            <span className="rounded-full border border-border bg-card px-2.5 py-1">{items.length} item{items.length === 1 ? '' : 's'} here</span>
            {info.expires_at && <span className="rounded-full border border-border bg-card px-2.5 py-1">Available until {formatDate(info.expires_at)}</span>}
            <span className="rounded-full border border-border bg-card px-2.5 py-1">{info.allow_download ? 'Download allowed' : 'View only'}</span>
          </div>
        </div>
        {info.allow_download && root && (
          <button
            type="button"
            onClick={() => download([root.id], true)}
            className="flex h-[52px] items-center gap-2.5 rounded-[14px] bg-filament px-5 text-base font-semibold text-filament-foreground shadow-[0_8px_22px_-10px_hsl(var(--filament))] hover:brightness-105"
          >
            <Download className="h-5 w-5" aria-hidden="true" /> Download everything (.zip)
          </button>
        )}
      </div>

      {listing?.breadcrumbs?.length > 1 && (
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
          {listing.breadcrumbs.map((c, i) => (
            <React.Fragment key={c.id}>
              {i > 0 && <ChevronRight className="h-4 w-4" aria-hidden="true" />}
              {i < listing.breadcrumbs.length - 1
                ? <button type="button" onClick={() => setFolderId(i === 0 ? null : c.id)} className="rounded px-1 hover:text-foreground">{c.name}</button>
                : <span className="px-1 font-semibold text-foreground">{c.name}</span>}
            </React.Fragment>
          ))}
        </nav>
      )}

      {info.allow_download && items.length > 0 && (
        <div className={`flex min-h-[56px] flex-wrap items-center gap-3 rounded-[14px] px-4 py-2 ${chosen.length ? 'bg-ink text-ink-foreground' : 'border border-border bg-card'}`}>
          <label className="flex cursor-pointer items-center gap-2.5 font-semibold">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={() => setSelected(allChecked ? new Set() : new Set(items.map((i) => i.id)))}
              className="h-5 w-5 accent-[hsl(var(--primary))]"
            />
            {chosen.length ? `${chosen.length} selected` : 'Select all'}
          </label>
          {chosen.length > 0 && (
            <>
              <span className="tabular text-xs opacity-80">{formatSize(totalSize)}{chosen.some((c) => c.kind === 'folder') ? ' + folders' : ''}</span>
              <div className="ml-auto flex gap-2">
                <button type="button" onClick={() => setSelected(new Set())} className="h-10 rounded-[10px] border border-current/40 px-3.5 font-semibold opacity-90">Clear</button>
                <button
                  type="button"
                  onClick={() => download(chosen.map((c) => c.id), chosen.length > 1 || chosen[0].kind === 'folder')}
                  className="flex h-10 items-center gap-2 rounded-[10px] bg-card px-4 font-bold text-foreground"
                >
                  <Download className="h-4 w-4" aria-hidden="true" /> Download selected
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {notice && <p role="status" className="rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground">{notice}</p>}

      {!listing && <div className="h-40 animate-pulse rounded-2xl bg-muted" />}
      {listing && items.length === 0 && (
        <div className="rounded-2xl border border-border bg-card px-6 py-14 text-center text-muted-foreground">This folder is empty.</div>
      )}
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-4">
        {items.map((item) => {
          const on = selected.has(item.id);
          return (
            <li key={item.id} className={`flex flex-col overflow-hidden rounded-2xl bg-card ${on ? 'border-2 border-primary' : 'border border-border'}`}>
              <div className="relative h-[132px]">
                <button type="button" onClick={() => openItem(item)} className="h-full w-full" aria-label={item.kind === 'folder' ? `Open ${item.name}` : `View ${item.name}`}>
                  <FileTag item={item} size="lg" />
                </button>
                {info.allow_download && (
                  <label className="absolute left-2 top-2 flex h-11 w-11 cursor-pointer items-center justify-center">
                    <input type="checkbox" checked={on} onChange={() => toggle(item.id)} aria-label={`Select ${item.name}`} className="h-[22px] w-[22px] accent-[hsl(var(--primary))]" />
                  </label>
                )}
              </div>
              <div className="flex items-center gap-2 px-3.5 py-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-semibold" title={item.name}>{item.name}</span>
                  <span className="tabular text-xs text-muted-foreground">{item.kind === 'folder' ? 'Folder' : formatSize(item.size)}</span>
                </div>
                {info.allow_download && (
                  <button
                    type="button"
                    onClick={() => download([item.id], item.kind === 'folder')}
                    aria-label={`Download ${item.name}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-border hover:bg-muted"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <GuestPreview item={preview} api={api} onClose={() => setPreview(null)} />
    </Shell>
  );
};

const GuestPreview = ({ item, api, onClose }) => {
  const [url, setUrl] = useState(null);
  const type = previewType(item);
  useEffect(() => {
    setUrl(null);
    if (item) api.downloadUrl([item.id], true).then(setUrl).catch(() => setUrl(''));
  }, [item, api]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[min(88vh,900px)] max-w-[min(1100px,calc(100vw-2rem))] flex-col gap-4 p-5">
        <DialogHeader>
          <DialogTitle className="truncate">{item?.name}</DialogTitle>
          <DialogDescription>{formatSize(item?.size)}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-muted">
          {url === '' && <p className="text-sm text-muted-foreground">This file can’t be previewed.</p>}
          {url === null && <p className="text-sm text-muted-foreground">Loading preview…</p>}
          {url && type === 'image' && <img src={url} alt={item.name} className="max-h-full max-w-full object-contain" />}
          {url && type === 'video' && <video src={url} controls className="max-h-full max-w-full" />}
          {url && type === 'audio' && <audio src={url} controls className="w-2/3" />}
          {url && (type === 'pdf' || type === 'text') && <iframe src={url} title={item.name} className="h-full w-full border-0 bg-white" />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuestPortal;
